import { useMemo } from 'react';
import { DRAINAGE_CLASSES, drainageClassForName, drainageClassOrder, isStreamLayer } from '../utils/hydrologyLegend';
import { isContourName } from '../utils/contourNames';
// Artifact type constants (from proto)
const ARTIFACT_TYPE_VECTOR = 2;
const formatContourLabel = (name) => {
    // Match patterns like "contours_0.5m_display.kml" or "contour_2ft.geojson"
    const contourMatch = name.match(/^contours?_([0-9.]+)(ft|m)(?:_display)?\.(?:kml|dxf|geojson|shp\.zip)$/i);
    if (contourMatch) {
        const value = contourMatch[1];
        const unit = contourMatch[2].toLowerCase();
        return `${value}${unit}`;
    }
    // Match patterns like "0.5m" or "2ft"
    const unitMatch = name.match(/^(\d+(?:\.\d+)?)\s*(m|ft)$/i);
    if (unitMatch) {
        return `${unitMatch[1]}${unitMatch[2].toLowerCase()}`;
    }
    // For names containing "contour", try to extract just the measurement
    const contourNameMatch = name.match(/(\d+(?:\.\d+)?)\s*(m|ft|meter|feet)/i);
    if (contourNameMatch) {
        const unit = contourNameMatch[2].toLowerCase().startsWith('f') ? 'ft' : 'm';
        return `${contourNameMatch[1]}${unit}`;
    }
    return name;
};
export const formatStreamLabel = (name) => { var _a, _b; return (_b = (_a = drainageClassForName(name)) === null || _a === void 0 ? void 0 : _a.shortLabel) !== null && _b !== void 0 ? _b : name; };
export const useLayerTypeDetection = (loadedArtifacts, isArtifactVisible, projectArtifacts) => {
    return useMemo(() => {
        var _a, _b;
        const contourLayers = [];
        const streamLayers = [];
        const streamOrder = new Map();
        const seenIds = new Set();
        // First, check project artifacts (all available layers)
        if (projectArtifacts && projectArtifacts.length > 0) {
            for (const artifact of projectArtifacts) {
                const artifactId = artifact.getId();
                const artifactType = artifact.getType();
                // Only look at vector artifacts
                if (artifactType !== ARTIFACT_TYPE_VECTOR)
                    continue;
                const name = artifact.getName();
                const metadataJson = artifact.getMetadata();
                const visible = isArtifactVisible(artifactId);
                seenIds.add(artifactId);
                if (isContourName(name)) {
                    contourLayers.push({ id: artifactId, label: formatContourLabel(name), visible });
                }
                else if (isStreamLayer(name, metadataJson)) {
                    streamLayers.push({ id: artifactId, label: formatStreamLabel(name), visible });
                    streamOrder.set(artifactId, drainageClassOrder(name));
                }
            }
        }
        // Fallback: also check loaded artifacts (for any that might not be in project structure)
        for (const [artifactId, artifact] of loadedArtifacts.entries()) {
            if (seenIds.has(artifactId))
                continue;
            if (artifact.type !== 'vector')
                continue;
            const name = typeof ((_a = artifact.metadata) === null || _a === void 0 ? void 0 : _a.getName) === 'function'
                ? artifact.metadata.getName()
                : '';
            const metadataJson = typeof ((_b = artifact.metadata) === null || _b === void 0 ? void 0 : _b.getMetadata) === 'function'
                ? artifact.metadata.getMetadata()
                : '';
            const visible = isArtifactVisible(artifactId);
            if (isContourName(name)) {
                contourLayers.push({ id: artifactId, label: formatContourLabel(name), visible });
            }
            else if (isStreamLayer(name, metadataJson)) {
                streamLayers.push({ id: artifactId, label: formatStreamLabel(name), visible });
                streamOrder.set(artifactId, drainageClassOrder(name));
            }
        }
        // Sort contour layers numerically ascending (e.g., 0.5m, 1m, 2m, 3m)
        contourLayers.sort((a, b) => {
            const numA = parseFloat(a.label) || 0;
            const numB = parseFloat(b.label) || 0;
            return numA - numB;
        });
        // Sort stream layers by conventional ordering
        streamLayers.sort((a, b) => { var _a, _b; return ((_a = streamOrder.get(a.id)) !== null && _a !== void 0 ? _a : DRAINAGE_CLASSES.length) - ((_b = streamOrder.get(b.id)) !== null && _b !== void 0 ? _b : DRAINAGE_CLASSES.length); });
        return {
            hasContourLayers: contourLayers.length > 0,
            hasStreamLayers: streamLayers.length > 0,
            contourLayers,
            streamLayers
        };
    }, [loadedArtifacts, isArtifactVisible, projectArtifacts]);
};
